import React, { useState } from 'react';
import { useUsers, ROLES, ROLE_LABELS } from '../../context/UsersContext';
import { useClients } from '../../context/ClientsContext';
import { C } from '../../utils/styles';
import Icon from '../Icon';
import { formatPhone } from '../../utils/formatters';
import StyledSelect from '../StyledSelect';

export default function EditUserModal({ user, onClose }) {
  const { updateUser, deactivateUser, reactivateUser, resetPassword, currentUser, getActiveUsers, isSuperUser, getUserById } = useUsers();
  const { clients, saveClient } = useClients();
  
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [role, setRole] = useState(user.role);
  const [title, setTitle] = useState(user.title || '');
  const [phone, setPhone] = useState(formatPhone(user.phone || ''));
  const [error, setError] = useState('');
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [newTempPassword, setNewTempPassword] = useState(null);
  const [reassignTo, setReassignTo] = useState('');

  const userClients = clients.filter(c => c.crm.assignedTo === user.id);
  const activeUsers = getActiveUsers().filter(u => u.id !== user.id);
  const invitedBy = getUserById(user.invitedBy);

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + 
      ' at ' + date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const handleSave = () => {
    setError('');

    // Validate role change
    if (user.role === ROLES.SUPER_USER && role === ROLES.ACCOUNT_MANAGER) {
      // Check if this is the last super user
      const superUserCount = getActiveUsers().filter(u => u.role === ROLES.SUPER_USER).length;
      if (superUserCount === 1) {
        setError('Cannot change the last Super User to Account Manager. Create another Super User first.');
        return;
      }
      
      if (!window.confirm(`This will restrict ${user.firstName} ${user.lastName} to only their assigned clients and remove admin access. Continue?`)) {
        return;
      }
    }

    if (user.role === ROLES.ACCOUNT_MANAGER && role === ROLES.SUPER_USER) {
      if (!window.confirm(`This will give ${user.firstName} ${user.lastName} full access to all clients and admin settings. Continue?`)) {
        return;
      }
    }

    updateUser(user.id, {
      firstName,
      lastName,
      role,
      title,
      phone,
    });

    onClose();
  };

  const handleResetPassword = async () => {
    const tempPass = await resetPassword(user.id);
    setNewTempPassword(tempPass);
    setShowResetConfirm(false);
  };

  const handleDeactivate = async () => {
    if (user.id === currentUser.id) {
      setError('You cannot deactivate your own account.');
      return;
    }

    if (userClients.length > 0 && !reassignTo) {
      setError('Please select a user to reassign the clients to.');
      return;
    }

    // Reassign clients
    if (userClients.length > 0 && reassignTo) {
      for (const client of userClients) {
        await saveClient({
          ...client,
          crm: {
            ...client.crm,
            assignedTo: reassignTo,
          }
        });
      }
    }

    await deactivateUser(user.id);
    setShowDeactivateConfirm(false);
    onClose();
  };

  const handleReactivate = async () => {
    await reactivateUser(user.id);
    onClose();
  };

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(newTempPassword);
    alert('Password copied to clipboard!');
  };

  // Show temp password after reset
  if (newTempPassword) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20,
        }}
        onClick={() => {
          setNewTempPassword(null);
          onClose();
        }}
      >
        <div
          style={{
            background: 'white',
            borderRadius: 12,
            padding: 32,
            maxWidth: 500,
            width: '100%',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, marginBottom: 16 }}>
            Password Reset
          </h2>
          <p style={{ fontSize: 13, color: C.textMid, marginBottom: 20 }}>
            A new temporary password has been generated for {user.firstName} {user.lastName}.
          </p>

          <div style={{
            background: C.blueLight,
            border: `2px solid ${C.blue}`,
            borderRadius: 8,
            padding: 16,
            marginBottom: 20,
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.textMid, marginBottom: 8, textTransform: 'uppercase' }}>
              New Temporary Password
            </div>
            <div style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 18,
              fontWeight: 700,
              color: C.text,
              marginBottom: 12,
            }}>
              {newTempPassword}
            </div>
            <button
              onClick={handleCopyPassword}
              style={{
                padding: '8px 14px',
                background: C.blue,
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Icon name="clipboard-outline" style={{ fontSize: 14 }} />
              Copy Password
            </button>
          </div>

          <button
            onClick={() => {
              setNewTempPassword(null);
              onClose();
            }}
            style={{
              width: '100%',
              padding: '10px',
              background: C.accent,
              color: 'white',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // Deactivate confirmation
  if (showDeactivateConfirm) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20,
        }}
        onClick={() => setShowDeactivateConfirm(false)}
      >
        <div
          style={{
            background: 'white',
            borderRadius: 12,
            padding: 28,
            maxWidth: 500,
            width: '100%',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, marginBottom: 16 }}>
            Deactivate {user.firstName} {user.lastName}?
          </h2>
          <p style={{ fontSize: 13, color: C.textMid, marginBottom: 20 }}>
            They will no longer be able to log in. {userClients.length > 0 && `Their ${userClients.length} client${userClients.length > 1 ? 's' : ''} will need to be reassigned.`}
          </p>

          {userClients.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
                Reassign {userClients.length} client{userClients.length > 1 ? 's' : ''} to: *
              </label>
              <StyledSelect
                value={reassignTo}
                onValueChange={setReassignTo}
                options={activeUsers.map(u => ({
                  value: u.id,
                  label: `${u.firstName} ${u.lastName} (${ROLE_LABELS[u.role]})`
                }))}
                placeholder="Select user..."
              />
            </div>
          )}

          {error && (
            <div style={{
              background: C.redLight,
              padding: 10,
              borderRadius: 6,
              fontSize: 12,
              color: C.red,
              fontWeight: 600,
              marginBottom: 16,
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => setShowDeactivateConfirm(false)}
              style={{
                flex: 1,
                padding: '10px',
                background: 'transparent',
                color: C.textMid,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleDeactivate}
              style={{
                flex: 1,
                padding: '10px',
                background: C.red,
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Deactivate User
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Reset password confirmation
  if (showResetConfirm) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20,
        }}
        onClick={() => setShowResetConfirm(false)}
      >
        <div
          style={{
            background: 'white',
            borderRadius: 12,
            padding: 28,
            maxWidth: 450,
            width: '100%',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, marginBottom: 16 }}>
            Reset Password?
          </h2>
          <p style={{ fontSize: 13, color: C.textMid, marginBottom: 20 }}>
            A new temporary password will be generated for {user.firstName} {user.lastName}. They will need to change it on next login.
          </p>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => setShowResetConfirm(false)}
              style={{
                flex: 1,
                padding: '10px',
                background: 'transparent',
                color: C.textMid,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleResetPassword}
              style={{
                flex: 1,
                padding: '10px',
                background: C.accent,
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Reset Password
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main edit form
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20,
        overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 12,
          padding: 28,
          maxWidth: 550,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          margin: '20px 0',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, marginBottom: 20 }}>
          Edit User: {user.firstName} {user.lastName}
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textMid, marginBottom: 6 }}>
              Email
            </label>
            <div style={{
              padding: '10px 14px',
              fontSize: 13,
              background: C.bg,
              color: C.textMid,
              borderRadius: 6,
              border: `1px solid ${C.border}`,
            }}>
              {user.email} (locked)
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label htmlFor="edit-user-firstname" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
                First Name
              </label>
              <input
                id="edit-user-firstname"
                name="first-name"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: 13,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  background: C.bg,
                  color: C.text,
                }}
              />
            </div>

            <div>
              <label htmlFor="edit-user-lastname" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
                Last Name
              </label>
              <input
                id="edit-user-lastname"
                name="last-name"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: 13,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  background: C.bg,
                  color: C.text,
                }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
              Role
            </label>
            <StyledSelect
              value={role}
              onValueChange={setRole}
              options={[
                { value: ROLES.ACCOUNT_MANAGER, label: "Account Manager" },
                { value: ROLES.SUPER_USER, label: "Super User" }
              ]}
            />
          </div>

          <div>
            <label htmlFor="edit-user-title" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
              Title
            </label>
            <input
              id="edit-user-title"
              name="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Senior Account Manager"
              autoComplete="organization-title"
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: 13,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                background: C.bg,
                color: C.text,
              }}
            />
          </div>

          <div>
            <label htmlFor="edit-user-phone" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
              Phone
            </label>
            <input
              id="edit-user-phone"
              name="phone"
              type="tel"
              value={phone}
              onChange={handlePhoneChange}
              placeholder="(555) 123-4567"
              maxLength={14}
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: 13,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                background: C.bg,
                color: C.text,
              }}
            />
          </div>
        </div>

        {/* User Stats */}
        <div style={{
          background: C.bg,
          borderRadius: 8,
          padding: 16,
          marginBottom: 20,
          fontSize: 12,
          color: C.textMid,
        }}>
          <div style={{ marginBottom: 8 }}>
            <strong>Status:</strong> {user.status === 'active' ? '🟢 Active' : user.status === 'invited' ? '🟡 Invited' : '⚫ Deactivated'}
          </div>
          <div style={{ marginBottom: 8 }}>
            <strong>Invited:</strong> {formatDate(user.invitedAt)} {invitedBy && `by ${invitedBy.firstName} ${invitedBy.lastName}`}
          </div>
          {user.joinedAt && (
            <div style={{ marginBottom: 8 }}>
              <strong>Joined:</strong> {formatDate(user.joinedAt)}
            </div>
          )}
          {user.lastLoginAt && (
            <div style={{ marginBottom: 8 }}>
              <strong>Last Login:</strong> {formatDateTime(user.lastLoginAt)}
            </div>
          )}
          <div style={{ marginBottom: 8 }}>
            <strong>Total Logins:</strong> {user.loginCount || 0}
          </div>
          <div>
            <strong>Assigned Clients:</strong> {userClients.length}
          </div>
        </div>

        {error && (
          <div style={{
            background: C.redLight,
            padding: 10,
            borderRadius: 6,
            fontSize: 12,
            color: C.red,
            fontWeight: 600,
            marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        {/* Actions */}
        <div style={{
          borderTop: `1px solid ${C.border}`,
          paddingTop: 16,
          marginBottom: 16,
          display: 'flex',
          gap: 10,
        }}>
          <button
            onClick={() => setShowResetConfirm(true)}
            style={{
              padding: '8px 14px',
              background: 'transparent',
              color: C.accent,
              border: `1px solid ${C.accent}`,
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Icon name="refresh-outline" style={{ fontSize: 14 }} />
            Reset Password
          </button>

          {user.status === 'active' && user.id !== currentUser.id && (
            <button
              onClick={() => setShowDeactivateConfirm(true)}
              style={{
                padding: '8px 14px',
                background: 'transparent',
                color: C.red,
                border: `1px solid ${C.red}`,
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Icon name="close-circle-outline" style={{ fontSize: 14 }} />
              Deactivate User
            </button>
          )}

          {user.status === 'deactivated' && (
            <button
              onClick={handleReactivate}
              style={{
                padding: '8px 14px',
                background: C.green,
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Icon name="checkmark-circle-outline" style={{ fontSize: 14 }} />
              Reactivate User
            </button>
          )}
        </div>

        {/* Save/Cancel */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '10px',
              background: 'transparent',
              color: C.textMid,
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              flex: 1,
              padding: '10px',
              background: C.accent,
              color: 'white',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
              <Icon name="save-outline" style={{ fontSize: 14 }} />
              Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
